import { Suspense, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Pagination,
    PaginationItem,
    Stack,
    Typography as MuiTypography,
    alpha,
    useTheme,
    type SxProps,
    type Theme,
} from "@mui/material";
import { Button, FoodsIcon, FoodsIconButton, FoodsPagination, SearchBox, Typography, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useDeleteTransportLeg, useTransportLegs } from "src/api/client-api/transport/useTransportLegs";
import { useDeleteTransportCircuit, useTransportCircuits } from "src/api/client-api/transport/useTransportCircuits";
import { TransportResourceCard } from "src/components/Transport/TransportResourceCard";
import { LazyTransportSelectionMap } from "src/components/Transport/Map";
import { URLHandler } from "src/services/URLHandler";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { GridPageHeader } from "src/components/Grid/PageGridHeader";
import { getTransportCircuitModeIcons, getTransportLegModeIcons } from "src/utils/transport/modeIcons";
import { getTransportCircuitMeta, getTransportLegMeta } from "src/utils/transport/resourceMeta";
import { TransportCreateChoiceModal } from "./TransportCreateChoiceModal";
import { TransportDeleteConfirmDialog } from "./TransportDeleteConfirmDialog";
import { type TransportResourceFilter, useTransportOverviewState } from "./transportOverviewState";
import { type TransportOverviewItem, useTransportOverviewResources } from "./useTransportOverviewResources";
import { useTransportAppearance, type TransportAppearance } from "./transportAppearance";
const filterIconByResource: Partial<Record<TransportResourceFilter, "leg" | "route">> = {
    legs: "leg",
    circuits: "route",
};

const TransportOverviewPanel = ({
    appearance,
    children,
    sx,
}: PropsWithChildren<{ appearance: TransportAppearance; sx: SxProps<Theme> }>) =>
    appearance === "legacy" ?
        <Card variant="outlined" sx={sx}>
            {children}
        </Card>
    :   <WhiteBox sx={sx}>{children}</WhiteBox>;

export const TransportOverview = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const appearance = useTransportAppearance();
    const overviewRootSx =
        appearance === "legacy" ?
            {
                mx: { xs: -3, md: -6 },
                my: -3,
                px: { xs: 3, md: 6 },
                py: 3,
                backgroundColor: theme.custom.grey[300],
            }
        :   {
                px: { xs: 3, md: 6 },
                py: 3,
                backgroundColor: theme.custom.grey[300],
            };
    const navigate = useNavigate();
    const { selectedCompany } = useFoodpilotContext();
    const orgId = selectedCompany?.id;
    const {
        search,
        setSearch,
        filter,
        setFilter,
        page,
        setPage,
        selectedIds,
        setSelectedIds,
        isMapExpanded,
        setIsMapExpanded,
    } = useTransportOverviewState();
    const mapPreviewAspectRatio = "334 / 235";
    const mapRailWidth = theme.spacing(isMapExpanded ? 56.1875 : 25.625);
    const [showCreateChoice, setShowCreateChoice] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<TransportOverviewItem | null>(null);
    const [deleteTargetOrganizationId, setDeleteTargetOrganizationId] = useState<number | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const {
        transportLegs: loadedTransportLegs,
        isLoading: legsLoading,
        isError: legsError,
    } = useTransportLegs({
        organizationId: orgId,
        enabled: orgId !== undefined,
    });
    const {
        transportCircuits: loadedTransportCircuits,
        isLoading: circuitsLoading,
        isError: circuitsError,
    } = useTransportCircuits({
        organizationId: orgId,
        enabled: orgId !== undefined,
    });
    const transportLegs = useMemo(() => loadedTransportLegs ?? [], [loadedTransportLegs]);
    const transportCircuits = useMemo(() => loadedTransportCircuits ?? [], [loadedTransportCircuits]);
    const { transportModes } = useSearchTransportModes();
    const deleteLeg = useDeleteTransportLeg();
    const deleteCircuit = useDeleteTransportCircuit();

    useEffect(() => {
        setShowCreateChoice(false);
        setDeleteTarget(null);
        setDeleteTargetOrganizationId(null);
        setDeleteError(null);
    }, [orgId]);

    const {
        filterButtons,
        items,
        mappableItemIds,
        paginatedItems,
        paginationPages,
        selectedCircuits,
        selectedLegs,
        totalPages,
        visibleItems,
    } = useTransportOverviewResources({
        t,
        transportLegs,
        transportCircuits,
        search,
        filter,
        page,
        setPage,
        selectedIds,
        setSelectedIds,
    });
    const isLoading = legsLoading || circuitsLoading;
    const hasLoadError = legsError || circuitsError;

    const toggleSelection = (id: string) => {
        setSelectedIds((current) =>
            current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id],
        );
    };

    const navigateToEdit = (item: TransportOverviewItem) => {
        if (orgId === undefined) return;

        if (item.kind === "leg") {
            navigate(URLHandler.transport.editLeg(orgId, item.leg.id));
            return;
        }

        navigate(URLHandler.transport.editCircuit(orgId, item.circuit.id));
    };

    const confirmDelete = async () => {
        if (deleteTarget === null || deleteTargetOrganizationId !== orgId) return;

        const targetId = deleteTarget.id;
        setDeleteError(null);

        try {
            if (deleteTarget.kind === "leg") {
                await deleteLeg.mutateAsync(deleteTarget.leg.id);
            } else {
                await deleteCircuit.mutateAsync(deleteTarget.circuit.id);
            }
        } catch {
            setDeleteError(
                t(
                    "generic.unexpected_error",
                    "An unexpected error occurred. Please try again later. You can contact our support team.",
                ),
            );
            return;
        }

        setSelectedIds((current) => current.filter((selectedId) => selectedId !== targetId));
        setDeleteTarget(null);
        setDeleteTargetOrganizationId(null);
    };

    if (orgId === undefined) {
        return (
            <Box data-appearance={appearance} data-testid="transport-overview" sx={overviewRootSx}>
                {appearance === "legacy" ?
                    <MuiTypography role="status" variant="body2">
                        {t("transport.no_org", "No organization selected")}
                    </MuiTypography>
                :   <Typography role="status" variant="body2">
                        {t("transport.no_org", "No organization selected")}
                    </Typography>
                }
            </Box>
        );
    }

    return (
        <Box data-appearance={appearance} data-testid="transport-overview" sx={overviewRootSx}>
            <Stack gap={3}>
                <Stack
                    data-testid="transport-overview-header"
                    direction={{ xs: "column", [appearance === "legacy" ? "lg" : "md"]: "row" }}
                    alignItems={{ [appearance === "legacy" ? "lg" : "md"]: "center" }}
                    gap={theme.spacing(appearance === "legacy" ? 3 : 2)}
                >
                    {appearance === "legacy" ?
                        <Stack gap={0.5} flex={1} minWidth={0}>
                            <MuiTypography component="h2" variant="h2">
                                {t("sidebar.transport.title", "Transport")}
                            </MuiTypography>
                            <MuiTypography
                                variant="body1"
                                sx={{ color: theme.custom.grey[2800], maxWidth: theme.spacing(95) }}
                            >
                                {t(
                                    "transport.overview.intro",
                                    "Manage your transport legs and itineraries to calculate the impact of transporting your products.",
                                )}
                            </MuiTypography>
                        </Stack>
                    :   <Stack gap={0} flex={1} minWidth={0}>
                            <GridPageHeader title={t("sidebar.transport.title", "Transport")} />
                            <Typography
                                variant="body-regular"
                                sx={{
                                    color: theme.custom.grey[2800],
                                    whiteSpace: { lg: "nowrap" },
                                }}
                            >
                                {t(
                                    "transport.overview.intro",
                                    "Manage your transport legs and itineraries to calculate the impact of transporting your products.",
                                )}
                            </Typography>
                        </Stack>
                    }

                    <Button
                        variant="primary"
                        startIcon={<FoodsIcon icon="plus" size={2} />}
                        onClick={() => setShowCreateChoice(true)}
                    >
                        {t("transport.overview.create_transport", "Create a new transport")}
                    </Button>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            ...(appearance === "legacy" ?
                                {
                                    xl:
                                        isMapExpanded ?
                                            "minmax(430px, 1fr) minmax(420px, 0.95fr)"
                                        :   "minmax(600px, 1fr) 300px",
                                }
                            :   { lg: `minmax(0, 1fr) ${mapRailWidth}` }),
                        },
                        gap: theme.spacing(3.125),
                        alignItems: "start",
                    }}
                    data-testid="transport-overview-panels"
                >
                    <TransportOverviewPanel
                        appearance={appearance}
                        sx={{
                            borderRadius: theme.spacing(1.5),
                            borderColor: theme.custom.grey[500],
                            backgroundColor: theme.custom.grey[100],
                            boxShadow: theme.custom.shadows.grey,
                            overflow: "hidden",
                        }}
                    >
                        <Stack gap={2} sx={{ p: 2 }}>
                            <Stack gap={1} sx={appearance === "legacy" ? { display: "contents" } : undefined}>
                                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} gap={2}>
                                    <Stack direction="row" gap={1.5} flexWrap="wrap" useFlexGap flex={1}>
                                        {filterButtons.map((button) => {
                                            const selected = filter === button.id;
                                            const icon = filterIconByResource[button.id];

                                            return (
                                                <Button
                                                    key={button.id}
                                                    variant={selected ? "tags-black" : "tags-white"}
                                                    startIcon={icon ? <FoodsIcon icon={icon} size={2} /> : undefined}
                                                    onClick={() => setFilter(button.id)}
                                                    sx={{
                                                        minHeight: theme.spacing(5),
                                                        borderRadius: theme.spacing(1),
                                                        px: icon ? 1.5 : 2,
                                                        boxShadow: theme.custom.shadows.grey,
                                                        borderColor: theme.custom.grey[500],
                                                        textTransform: "none",
                                                    }}
                                                >
                                                    {button.label}
                                                </Button>
                                            );
                                        })}
                                    </Stack>

                                    {isLoading &&
                                        (appearance === "legacy" ?
                                            <CircularProgress size={22} />
                                        :   <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />)}
                                </Stack>

                                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} gap={2}>
                                    {appearance === "legacy" ?
                                        <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2800], flex: 1 }}>
                                            {t(
                                                "transport.overview.selected_on_map_count",
                                                "{{selected}}/{{total}} transports displayed on the map",
                                                {
                                                    selected: selectedIds.length,
                                                    total: items.length,
                                                },
                                            )}
                                        </MuiTypography>
                                    :   <Typography variant="body2" sx={{ color: theme.custom.grey[2800], flex: 1 }}>
                                            {t(
                                                "transport.overview.selected_on_map_count",
                                                "{{selected}}/{{total}} transports displayed on the map",
                                                {
                                                    selected: selectedIds.length,
                                                    total: items.length,
                                                },
                                            )}
                                        </Typography>
                                    }

                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<FoodsIcon icon="plus" size={1.6} />}
                                        onClick={() => {
                                            setSelectedIds(mappableItemIds);
                                        }}
                                        sx={
                                            appearance === "product-v2" ?
                                                { height: "auto", minHeight: "auto", p: 0 }
                                            :   undefined
                                        }
                                    >
                                        {t("transport.overview.show_all", "Show all")}
                                    </Button>
                                </Stack>
                            </Stack>

                            <SearchBox
                                variant="standalone"
                                value={search}
                                placeholder={t("transport.search.placeholder", "Search transport")}
                                onChange={setSearch}
                            />

                            {hasLoadError &&
                                (appearance === "legacy" ?
                                    <Alert severity="error">
                                        {t(
                                            "generic.unexpected_error",
                                            "An unexpected error occurred. Please try again later. You can contact our support team.",
                                        )}
                                    </Alert>
                                :   <RemoteErrorAlert
                                        message={t(
                                            "generic.unexpected_error",
                                            "An unexpected error occurred. Please try again later. You can contact our support team.",
                                        )}
                                        status="server-error"
                                    />)}

                            <Stack
                                gap={2}
                                sx={{
                                    maxHeight:
                                        appearance === "legacy" ?
                                            { xs: "none", xl: `calc(100vh - ${theme.spacing(45)})` }
                                        :   { xs: "none", lg: `calc(100vh - ${theme.spacing(45)})` },
                                    overflow: "auto",
                                }}
                            >
                                {!isLoading && !hasLoadError && visibleItems.length === 0 && (
                                    <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                                        {appearance === "legacy" ?
                                            <MuiTypography variant="body2">
                                                {t(
                                                    "transport.overview.empty_search",
                                                    "No leg or itinerary matches the current search.",
                                                )}
                                            </MuiTypography>
                                        :   <Typography variant="body2">
                                                {t(
                                                    "transport.overview.empty_search",
                                                    "No leg or itinerary matches the current search.",
                                                )}
                                            </Typography>
                                        }
                                    </Box>
                                )}

                                {paginatedItems.map((item) => (
                                    <TransportResourceCard
                                        key={item.id}
                                        appearance={appearance}
                                        kind={item.kind}
                                        title={item.title}
                                        subtitle={
                                            appearance === "legacy" ?
                                                `${item.route.origin} -> ${item.route.destination}`
                                            :   undefined
                                        }
                                        route={appearance === "product-v2" ? item.route : undefined}
                                        modeIcons={
                                            item.kind === "leg" ?
                                                getTransportLegModeIcons(item.leg, transportModes)
                                            :   getTransportCircuitModeIcons(item.circuit, transportModes)
                                        }
                                        selected={selectedIds.includes(item.id)}
                                        onSelect={() => toggleSelection(item.id)}
                                        actions={
                                            <>
                                                <FoodsIconButton
                                                    size="s"
                                                    icon="delete"
                                                    tooltip={{ text: t("common.action.delete", "Delete") }}
                                                    buttonProps={{
                                                        "aria-label": `${t("common.action.delete", "Delete")} ${item.title}`,
                                                        onClick: () => {
                                                            if (orgId === undefined) return;

                                                            setDeleteError(null);
                                                            setDeleteTarget(item);
                                                            setDeleteTargetOrganizationId(orgId);
                                                        },
                                                        disabled: deleteLeg.isLoading || deleteCircuit.isLoading,
                                                    }}
                                                />
                                                <FoodsIconButton
                                                    size="s"
                                                    icon="edit"
                                                    tooltip={{ text: t("common.action.edit", "Edit") }}
                                                    buttonProps={{
                                                        "aria-label": `${t("common.action.edit", "Edit")} ${item.title}`,
                                                        onClick: () => navigateToEdit(item),
                                                    }}
                                                />
                                            </>
                                        }
                                        meta={
                                            item.kind === "leg" ?
                                                getTransportLegMeta(item.leg, t)
                                            :   getTransportCircuitMeta(item.circuit, t)
                                        }
                                    />
                                ))}
                            </Stack>

                            {totalPages > 1 && (
                                <Stack alignItems="flex-end" sx={{ pt: 0.5 }}>
                                    {appearance === "legacy" ?
                                        <Pagination
                                            aria-label={t("transport.overview.pagination", "Transport pages")}
                                            count={totalPages}
                                            page={page}
                                            onChange={(_event, nextPage) => setPage(nextPage)}
                                            siblingCount={1}
                                            boundaryCount={1}
                                            shape="circular"
                                            renderItem={(item) => (
                                                <PaginationItem
                                                    {...item}
                                                    slots={{
                                                        previous: () => <FoodsIcon icon="arrowLeft" size={2} />,
                                                        next: () => <FoodsIcon icon="arrowRight" size={2} />,
                                                    }}
                                                />
                                            )}
                                            sx={{
                                                "& .MuiPagination-ul": {
                                                    gap: theme.spacing(1),
                                                    justifyContent: "flex-end",
                                                },
                                                "& .MuiPaginationItem-root": {
                                                    width: theme.spacing(4),
                                                    height: theme.spacing(4),
                                                    minWidth: theme.spacing(4),
                                                    m: 0,
                                                    border: 0,
                                                    borderRadius: "50%",
                                                    color: theme.custom.grey[2800],
                                                    fontSize: theme.typography.pxToRem(14),
                                                    fontWeight: 700,
                                                    lineHeight: "160%",
                                                },
                                                "& .MuiPaginationItem-root.Mui-selected": {
                                                    backgroundColor: theme.palette.primary[500],
                                                    color: theme.custom.grey[100],
                                                    "&:hover": {
                                                        backgroundColor: theme.palette.primary[500],
                                                    },
                                                },
                                                "& .MuiPaginationItem-previousNext": {
                                                    color: theme.custom.grey[2800],
                                                },
                                                "& .MuiPaginationItem-previousNext.Mui-disabled": {
                                                    opacity: 0.36,
                                                },
                                                "& svg path": {
                                                    fill: "currentColor",
                                                },
                                            }}
                                        />
                                    :   <FoodsPagination
                                            pages={paginationPages}
                                            page={page}
                                            setPage={setPage}
                                            PaginationProps={{
                                                "aria-label": t("transport.overview.pagination", "Transport pages"),
                                            }}
                                        />
                                    }
                                </Stack>
                            )}
                        </Stack>
                    </TransportOverviewPanel>

                    <TransportOverviewPanel
                        appearance={appearance}
                        sx={{
                            borderRadius: theme.spacing(1.5),
                            borderColor: theme.custom.grey[500],
                            backgroundColor: theme.custom.grey[100],
                            boxShadow: theme.custom.shadows.grey,
                            p: appearance === "legacy" ? theme.spacing(2) : theme.spacing(1.875),
                            position: { [appearance === "legacy" ? "xl" : "lg"]: "sticky" },
                            top: { [appearance === "legacy" ? "xl" : "lg"]: theme.spacing(3) },
                        }}
                    >
                        <Stack gap={2}>
                            <Stack gap={appearance === "legacy" ? 0.5 : 0}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                                    {appearance === "legacy" ?
                                        <MuiTypography component="h3" variant="h3" sx={{ fontSize: theme.spacing(2) }}>
                                            {t("transport.map.preview_title", "Transport map")}
                                        </MuiTypography>
                                    :   <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <Typography variant="big-semibold">
                                                {t("transport.map.preview_title", "Transport map")}
                                            </Typography>
                                        </Box>
                                    }
                                    {appearance === "legacy" && selectedIds.length > 0 && (
                                        <Button size="small" variant="text" onClick={() => setSelectedIds([])}>
                                            {t("transport.overview.clear_selection", "Clear")}
                                        </Button>
                                    )}
                                </Stack>
                                {isMapExpanded &&
                                    (appearance === "legacy" ?
                                        <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2800] }}>
                                            {t(
                                                "transport.overview.map_help",
                                                "Use the toggle button on the left panel to visualize your legs and itineraries on the map.",
                                            )}
                                        </MuiTypography>
                                    :   <Typography variant="body2" sx={{ color: theme.custom.grey[2800] }}>
                                            {t(
                                                "transport.overview.map_help",
                                                "Use the toggle button on the left panel to visualize your legs and itineraries on the map.",
                                            )}
                                        </Typography>)}
                            </Stack>

                            <Box
                                sx={{
                                    borderRadius: theme.spacing(1.5),
                                    overflow: "hidden",
                                    backgroundColor:
                                        appearance === "legacy" ?
                                            alpha(theme.custom.grey[500], 0.35)
                                        :   theme.custom.grey[100],
                                }}
                            >
                                {appearance === "legacy" ?
                                    <Suspense
                                        fallback={
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                minHeight={isMapExpanded ? 294 : 122}
                                            >
                                                <CircularProgress />
                                            </Box>
                                        }
                                    >
                                        <LazyTransportSelectionMap
                                            legs={selectedLegs}
                                            circuits={selectedCircuits}
                                            heightPx={isMapExpanded ? 294 : 122}
                                        />
                                    </Suspense>
                                : isLoading ?
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ aspectRatio: mapPreviewAspectRatio }}
                                    >
                                        <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                    </Box>
                                :   <Suspense
                                        fallback={
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                sx={{ aspectRatio: mapPreviewAspectRatio }}
                                            >
                                                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                            </Box>
                                        }
                                    >
                                        <LazyTransportSelectionMap
                                            key={isMapExpanded ? "expanded" : "reduced"}
                                            legs={selectedLegs}
                                            circuits={selectedCircuits}
                                            compact={!isMapExpanded}
                                            aspectRatio={mapPreviewAspectRatio}
                                        />
                                    </Suspense>
                                }
                            </Box>

                            <Button
                                size="small"
                                variant="text"
                                startIcon={
                                    <FoodsIcon icon={isMapExpanded ? "rightPanelClose" : "rightPanelOpen"} size={1.6} />
                                }
                                onClick={() => setIsMapExpanded((current) => !current)}
                                sx={
                                    appearance === "legacy" ?
                                        { alignSelf: "flex-start" }
                                    :   { alignSelf: "flex-start", height: "auto", minHeight: "auto", p: 0 }
                                }
                            >
                                {isMapExpanded ?
                                    t("transport.overview.reduce_map_panel", "Reduce map panel")
                                :   t("transport.overview.expand_map_panel", "Expand map panel")}
                            </Button>
                        </Stack>
                    </TransportOverviewPanel>
                </Box>

                <TransportCreateChoiceModal
                    appearance={appearance}
                    open={showCreateChoice}
                    onClose={() => setShowCreateChoice(false)}
                    onSelectLeg={() => navigate(URLHandler.transport.newLeg(orgId))}
                    onSelectItinerary={() => navigate(URLHandler.transport.newCircuit(orgId))}
                />
                <TransportDeleteConfirmDialog
                    appearance={appearance}
                    open={deleteTarget !== null}
                    title={deleteTarget?.title}
                    error={deleteError}
                    isDeleting={deleteLeg.isLoading || deleteCircuit.isLoading}
                    onClose={() => {
                        setDeleteError(null);
                        setDeleteTarget(null);
                        setDeleteTargetOrganizationId(null);
                    }}
                    onConfirm={() => void confirmDelete()}
                />
            </Stack>
        </Box>
    );
};

export default TransportOverview;
